import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { BrevoList } from '../../queries/brevoLists';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { cn } from '../../lib/utils';

interface CampaignSendPanelProps {
  status: string;
  testPassedAt: string | null | undefined;
  listOptions: BrevoList[];
  selectedListIds: number[];
  onSelectedListIdsChange: (ids: number[]) => void;
  recipientCount: number | undefined;
  onSendTest: (testEmails: string[]) => void;
  sendTestPending: boolean;
  sendTestError: string | null;
  onSend: (overrideSent: boolean) => void;
  sendPending: boolean;
  sendError: string | null;
  // When false, a test send is encouraged but not required to send to the
  // audience (marketing campaigns). Defaults to true (tips emails keep the gate).
  testRequired?: boolean;
  // Set when something about the current form state makes sending wrong
  // (unsaved edits, a promo campaign whose HTML has lost its merge tag).
  // Disables both sends and shows the reason. The server re-checks anyway.
  blockedReason?: string;
}

// The safety-gated send UI shared by MarketingCampaignsPage and
// EmailCampaignsPage. "Send to audience" stays disabled until a test send
// has succeeded for the *current* content - mirrors the backend's own gate
// (PUT clears testPassedAt on any edit), which is the real enforcement;
// this is defence in depth, not the only check.
export function CampaignSendPanel({
  status,
  testPassedAt,
  listOptions,
  selectedListIds,
  onSelectedListIdsChange,
  recipientCount,
  onSendTest,
  sendTestPending,
  sendTestError,
  onSend,
  sendPending,
  sendError,
  testRequired = true,
  blockedReason,
}: CampaignSendPanelProps) {
  const [testEmailsInput, setTestEmailsInput] = useState('');
  const [confirmingSend, setConfirmingSend] = useState(false);
  const alreadySent = status === 'sent';
  const untested = !testPassedAt;

  const toggleList = (id: number) => {
    onSelectedListIdsChange(selectedListIds.includes(id) ? selectedListIds.filter((l) => l !== id) : [...selectedListIds, id]);
  };

  const handleSendTest = () => {
    const testEmails = testEmailsInput
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    if (testEmails.length === 0 || selectedListIds.length === 0 || blockedReason) return;
    onSendTest(testEmails);
  };

  return (
    <Card className="space-y-4 p-4">
      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Target lists</p>
        <div className="flex flex-wrap gap-2">
          {listOptions.map((list) => (
            <button
              key={list.id}
              type="button"
              onClick={() => toggleList(list.id)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                selectedListIds.includes(list.id) ? 'border-primary bg-primary/10 text-primary' : 'border-input text-muted-foreground hover:border-foreground/30',
              )}
            >
              {list.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Send test</p>
        <div className="flex items-center gap-2">
          <input
            value={testEmailsInput}
            onChange={(e) => setTestEmailsInput(e.target.value)}
            placeholder="test1@example.com, test2@example.com"
            className="min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <Button variant="outline" size="sm" onClick={handleSendTest} disabled={sendTestPending || !testEmailsInput.trim() || selectedListIds.length === 0 || Boolean(blockedReason)}>
            {sendTestPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Send Test
          </Button>
        </div>
        {testPassedAt ? (
          <p className="mt-1.5 text-xs text-emerald-600 dark:text-emerald-400">Tested successfully at {new Date(testPassedAt).toLocaleString()}</p>
        ) : (
          <p className="mt-1.5 text-xs text-muted-foreground">
            Run a test send to a private address before sending to the audience{testRequired ? '' : ' (optional)'}.
          </p>
        )}
        {sendTestError && <p className="mt-1 text-xs text-destructive">{sendTestError}</p>}
      </div>

      <div>
        <Button
          onClick={() => selectedListIds.length > 0 && !blockedReason && setConfirmingSend(true)}
          disabled={sendPending || selectedListIds.length === 0 || (testRequired && untested) || Boolean(blockedReason)}
        >
          {sendPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {alreadySent ? 'Resend to Audience' : 'Send to Audience'}
        </Button>
        {recipientCount !== undefined && selectedListIds.length > 0 && (
          <span className="ml-2 text-xs text-muted-foreground">{recipientCount} real recipients</span>
        )}
        {blockedReason && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{blockedReason}</p>}
        {sendError && <p className="mt-1 text-xs text-destructive">{sendError}</p>}
      </div>

      <ConfirmDialog
        open={confirmingSend}
        onOpenChange={setConfirmingSend}
        title={alreadySent ? 'Resend campaign?' : 'Send campaign?'}
        description={
          `${
            alreadySent
              ? `This campaign was already sent. Resend to ${recipientCount ?? '?'} recipients across ${selectedListIds.length} list(s)?`
              : `Send to ${recipientCount ?? '?'} recipients across ${selectedListIds.length} list(s)?`
          }${untested ? ' No test send has been run for this content.' : ''} This cannot be undone.`
        }
        confirmLabel={alreadySent ? 'Resend' : 'Send'}
        destructive={alreadySent}
        busy={sendPending}
        onConfirm={() => {
          setConfirmingSend(false);
          onSend(alreadySent);
        }}
      />
    </Card>
  );
}
